import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import facturaVentaService, { FacturaVenta } from '../../services/facturaVentaService';
import notaCreditoVentaService from '../../services/notaCreditoVentaService';
import productoService from '../../services/productoService';
import { calcularTotalesIvaVenta } from '../../utils/calcularTotalesIvaVenta';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatCurrency } from '../../utils/formatters';
import {
  facturaToNotaCreditoPosState,
  notaCreditoPosToCreateDto,
  parseNroUbicacionFromLabel,
  type NotaCreditoFacturaTotalesRef,
  type NotaCreditoPosCabecera,
} from '../../utils/facturaToNotaCreditoPos';
import FacturaPOSClientePanel from '../facturaventa/pos/FacturaPOSClientePanel';
import FacturaPOSLineasGrid from '../facturaventa/pos/FacturaPOSLineasGrid';
import FacturaPOSTotalesPanel, { type POSCompaniaOption } from '../facturaventa/pos/FacturaPOSTotalesPanel';
import companiaService from '../../services/companiaService';
import { POSCliente, POSLinea } from '../facturaventa/pos/types';
import { obtenerCodigoListaCliente } from '../../utils/precioListaPedido';

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number => {
  const base = l.cantidad * l.precioUnitario;
  return Math.round(base * (1 - l.descuento / 100) * 100) / 100;
};

interface SucursalVenta {
  nroSuc: number;
  nombreSucursal?: string;
}

const NuevaNotaCreditoPOS: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const facturaIdParam = searchParams.get('facturaId');
  const facturaId = facturaIdParam ? parseInt(facturaIdParam, 10) : null;

  const [loadingFactura, setLoadingFactura] = useState(false);
  const [errorFactura, setErrorFactura] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [posCliente, setPosCliente] = useState<POSCliente | null>(null);
  const [lineas, setLineas] = useState<POSLinea[]>([]);
  const [cabecera, setCabecera] = useState<NotaCreditoPosCabecera | null>(null);
  const [tituloFactura, setTituloFactura] = useState('');
  const [facturaTotales, setFacturaTotales] = useState<NotaCreditoFacturaTotalesRef | undefined>();
  const [productosIvaCache, setProductosIvaCache] = useState<Record<string, unknown>>({});

  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [loadingSuc, setLoadingSuc] = useState(false);
  const [companias, setCompanias] = useState<POSCompaniaOption[]>([]);
  const [loadingCompanias, setLoadingCompanias] = useState(true);

  const [selectorOpen, setSelectorOpen] = useState(false);
  const [facturas, setFacturas] = useState<FacturaVenta[]>([]);
  const [loadingFacturas, setLoadingFacturas] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLetra, setSearchLetra] = useState('');
  const [searchNroSuc, setSearchNroSuc] = useState('');
  const [searchNroComprobante, setSearchNroComprobante] = useState('');
  const [searchNroCliente, setSearchNroCliente] = useState('');
  const [fechaDesde, setFechaDesde] = useState(() =>
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
  );
  const [fechaHasta, setFechaHasta] = useState(() =>
    new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
  );
  const [totalFacturas, setTotalFacturas] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const desdeFactura = Boolean(cabecera?.nroFormularioFactura && cabecera.nroFormularioFactura > 0);
  const listo = Boolean(cabecera && posCliente);

  useEffect(() => {
    let cancelled = false;
    const cargar = async () => {
      setLoadingSuc(true);
      setLoadingCompanias(true);
      try {
        const [resSuc, listaCompanias] = await Promise.all([
          api.get('/api/SucursalesVenta'),
          companiaService.getCompanias({ pageNumber: 1, pageSize: 100 }),
        ]);
        if (cancelled) return;
        const sucData: SucursalVenta[] = Array.isArray(resSuc.data)
          ? resSuc.data
          : resSuc.data?.items ?? [];
        setSucursales(sucData);
        const comData: POSCompaniaOption[] = listaCompanias
          .map((c) => ({
            idCompania: String(c.idCompania ?? '').trim(),
            nombre: String(c.nombre ?? '').trim() || undefined,
          }))
          .filter((c) => Boolean(c.idCompania));
        setCompanias(comData);
      } catch {
        if (!cancelled) {
          setSucursales([]);
          setCompanias([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingSuc(false);
          setLoadingCompanias(false);
        }
      }
    };
    void cargar();
    return () => {
      cancelled = true;
    };
  }, []);

  /** La factura GET no trae idCompania; hay que asignarlo cuando exista cabecera y catálogo. */
  useEffect(() => {
    if (companias.length === 0 || !cabecera) return;
    const current = String(cabecera.idCompania ?? '').trim();
    if (current && companias.some((c) => c.idCompania === current)) return;
    const primera = companias[0]?.idCompania;
    if (!primera || cabecera.idCompania === primera) return;
    setCabecera((prev) => (prev ? { ...prev, idCompania: primera } : prev));
  }, [companias, cabecera, cabecera?.idCompania]);

  const applyPosState = useCallback(
    (state: Awaited<ReturnType<typeof facturaToNotaCreditoPosState>>) => {
      setPosCliente(state.posCliente);
      setLineas(state.lineas);
      setCabecera(state.cabecera);
      setTituloFactura(state.tituloFactura ?? '');
      setFacturaTotales(state.facturaTotales);
      setSelectorOpen(false);
    },
    [],
  );

  const loadFactura = useCallback(
    async (nroFormulario: number) => {
      setLoadingFactura(true);
      setErrorFactura(null);
      try {
        const data = await facturaVentaService.getFacturaVenta(nroFormulario);
        const raw = data as unknown as Record<string, unknown>;
        const facturaWithExt = {
          ...data,
          ordenCompra: String(raw.ordenDeCompra ?? data.ordenCompra ?? ''),
          formaDePago: data.formaDePago ?? '',
          nroUbicacionOrigen: raw.nroUbicacionOrigen as number | undefined,
          formaEntrega: String(raw.formaEntrega ?? raw.transporte ?? ''),
        };
        const state = await facturaToNotaCreditoPosState(facturaWithExt);
        applyPosState(state);
      } catch (err: unknown) {
        const msg = getApiErrorMessage(err, 'Error al cargar la factura');
        setErrorFactura(msg);
        enqueueSnackbar(msg, { variant: 'error' });
      } finally {
        setLoadingFactura(false);
      }
    },
    [applyPosState, enqueueSnackbar],
  );

  useEffect(() => {
    if (!facturaId || Number.isNaN(facturaId)) return;
    void loadFactura(facturaId);
  }, [facturaId, loadFactura]);

  useEffect(() => {
    if (!selectorOpen) return;
    setPage(0);
  }, [selectorOpen]);

  useEffect(() => {
    if (!selectorOpen) return;
    setPage(0);
  }, [searchTerm, searchLetra, searchNroSuc, searchNroComprobante, searchNroCliente, fechaDesde, fechaHasta]);

  useEffect(() => {
    if (!selectorOpen) return;
    let cancelled = false;
    setLoadingFacturas(true);
    const nroSucNum = searchNroSuc.trim() ? parseInt(searchNroSuc, 10) : undefined;
    const nroCompNum = searchNroComprobante.trim() ? parseInt(searchNroComprobante, 10) : undefined;
    const nroClienteNum = searchNroCliente.trim() ? parseInt(searchNroCliente, 10) : undefined;
    facturaVentaService
      .getFacturasVenta({
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        searchTerm: searchTerm || undefined,
        letra: searchLetra.trim() || undefined,
        nroSuc: Number.isFinite(nroSucNum) ? nroSucNum : undefined,
        nroComprobante: Number.isFinite(nroCompNum) ? nroCompNum : undefined,
        nroCliente: Number.isFinite(nroClienteNum) ? nroClienteNum : undefined,
        fechaDesde: fechaDesde ? new Date(fechaDesde) : undefined,
        fechaHasta: fechaHasta ? new Date(fechaHasta) : undefined,
      })
      .then((res) => {
        if (!cancelled) {
          setFacturas(res.items || []);
          setTotalFacturas(res.totalCount ?? 0);
        }
      })
      .catch(() => {
        if (!cancelled) setFacturas([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingFacturas(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectorOpen,
    searchTerm,
    searchLetra,
    searchNroSuc,
    searchNroComprobante,
    searchNroCliente,
    fechaDesde,
    fechaHasta,
    page,
    rowsPerPage,
  ]);

  const codigosLineasKey = lineas.map((l) => String(l.codigoArticulo ?? '').trim()).join(',');

  useEffect(() => {
    const codigos = lineas
      .map((l) => String(l.codigoArticulo ?? '').trim())
      .filter((c): c is string => !!c);
    if (codigos.length === 0) return;

    let cancelled = false;
    void (async () => {
      const loaded: Record<string, unknown> = {};
      await Promise.all(
        codigos.map(async (codigo) => {
          try {
            loaded[codigo] = await productoService.getProducto(codigo);
          } catch {
            /* sin datos IVA */
          }
        }),
      );
      if (!cancelled && Object.keys(loaded).length > 0) {
        setProductosIvaCache((prev) => {
          const next = { ...prev };
          let changed = false;
          for (const [codigo, producto] of Object.entries(loaded)) {
            if (!next[codigo]) {
              next[codigo] = producto;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [codigosLineasKey, lineas]);

  const handleLineaChange = useCallback((id: string, field: keyof POSLinea, value: string | number) => {
    setLineas((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, [field]: value };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  }, []);

  const handleLineaFilled = useCallback((id: string, data: Partial<POSLinea>) => {
    setLineas((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, ...data };
        updated.subtotal = calcularSubtotal(updated);
        return updated;
      }),
    );
  }, []);

  const handleEliminarLinea = useCallback((id: string) => {
    setLineas((prev) => prev.filter((l) => l.id !== id));
  }, []);

  const handleAgregarLineaVacia = useCallback(() => {
    setLineas((prev) => [
      ...prev,
      {
        id: genId(),
        codigoArticulo: '',
        descripcionArticulo: '',
        cantidad: 1,
        precioUnitario: 0,
        descuento: 0,
        subtotal: 0,
        um: '',
      },
    ]);
  }, []);

  const handleDuplicarLinea = useCallback((id: string) => {
    setLineas((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const copia: POSLinea = { ...prev[idx], id: genId() };
      const next = [...prev];
      next.splice(idx + 1, 0, copia);
      return next;
    });
  }, []);

  const registrarProductoIva = useCallback((codigoArticulo: string, producto: unknown) => {
    const cod = String(codigoArticulo ?? '').trim();
    if (!cod || !producto) return;
    setProductosIvaCache((prev) => (prev[cod] === producto ? prev : { ...prev, [cod]: producto }));
  }, []);

  const tipoIvaReceptor = String(posCliente?.tipoIva ?? '').trim();
  const subtotalNeto = lineas.reduce((acc, l) => acc + l.subtotal, 0);
  const totalesIva = useMemo(
    () =>
      calcularTotalesIvaVenta({
        lineas: lineas.map((l) => ({ codigoArticulo: l.codigoArticulo, subtotal: l.subtotal })),
        subtotalNeto,
        subtotalConDesc: subtotalNeto,
        tipoIva: tipoIvaReceptor,
        productosCache: productosIvaCache,
      }),
    [lineas, subtotalNeto, tipoIvaReceptor, productosIvaCache],
  );

  const handleGuardar = async () => {
    if (!cabecera || !posCliente) {
      enqueueSnackbar('Cargue una factura origen', { variant: 'warning' });
      return;
    }
    if (cabecera.nroSuc <= 0) {
      enqueueSnackbar('Seleccioná una sucursal', { variant: 'warning' });
      return;
    }
    if (!cabecera.fechaEmision?.trim()) {
      enqueueSnackbar('Ingresá la fecha de emisión', { variant: 'warning' });
      return;
    }
    const nUb = parseNroUbicacionFromLabel(cabecera.ubicacion);
    if (!nUb) {
      enqueueSnackbar('Seleccioná una ubicación / depósito de origen', { variant: 'warning' });
      return;
    }
    const lineasValidas = lineas.filter((l) => l.codigoArticulo.trim() && l.cantidad > 0);
    if (lineasValidas.length === 0) {
      enqueueSnackbar('Agregá al menos un artículo con cantidad', { variant: 'warning' });
      return;
    }
    const idCompania =
      String(cabecera.idCompania ?? '').trim() ||
      String(companias[0]?.idCompania ?? '').trim();
    if (!idCompania) {
      enqueueSnackbar('Seleccioná una compañía', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const payload = notaCreditoPosToCreateDto({
        posCliente,
        lineas: lineasValidas,
        cabecera: { ...cabecera, idCompania },
      });
      await notaCreditoVentaService.createNotaCredito(payload);
      enqueueSnackbar('Nota de crédito registrada correctamente', { variant: 'success' });
      navigate('/ventas/notas-credito');
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'Error al guardar la nota de crédito');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const handleSelectFactura = (nroFormulario: number) => {
    setSearchParams({ facturaId: String(nroFormulario) });
  };

  const formatDate = (d: string | Date | undefined) => {
    if (!d) return '-';
    try {
      const date = typeof d === 'string' ? new Date(d) : d;
      return date.toLocaleDateString('es-AR');
    } catch {
      return '-';
    }
  };

  const formatComprobante = (f: FacturaVenta) => {
    const letra = f.letra?.trim().toUpperCase() || '';
    const suc = f.nroSuc != null ? String(f.nroSuc).padStart(5, '0') : '';
    const comp = f.nroComprobante != null ? String(f.nroComprobante).padStart(8, '0') : '';
    if (!letra && !suc && !comp) return '-';
    return `${letra || '-'}-${suc || '-'}-${comp || '-'}`;
  };

  const updateCabecera = useCallback((patch: Partial<NotaCreditoPosCabecera>) => {
    setCabecera((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const handleIdCompaniaChange = useCallback(
    (id: string) => updateCabecera({ idCompania: id }),
    [updateCabecera],
  );

  const nroLista = obtenerCodigoListaCliente(posCliente) || undefined;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1, sm: 2 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} mb={2.5} flexWrap="wrap">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            window.history.length > 1 ? navigate(-1) : navigate('/ventas/notas-credito')
          }
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Notas de crédito
        </Button>
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Typography variant="h5" fontWeight={700} color="primary.main">
          Nueva Nota de Crédito POS
        </Typography>
        <Chip label="POS" size="small" color="primary" variant="outlined" />
        {tituloFactura ? (
          <Chip label={`Factura origen: ${tituloFactura}`} size="small" color="info" variant="outlined" />
        ) : null}
      </Stack>

      {errorFactura && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorFactura}
        </Alert>
      )}

      {!facturaId && !listo && (
        <Box sx={{ mb: 2 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Seleccioná la factura de venta sobre la cual deseás emitir la nota de crédito.
          </Typography>
          <Button variant="contained" color="primary" onClick={() => setSelectorOpen(true)}>
            Seleccionar factura
          </Button>
        </Box>
      )}

      {facturaId && loadingFactura && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
          <CircularProgress size={28} />
          <Typography color="text.secondary">Cargando factura...</Typography>
        </Box>
      )}

      {listo && !loadingFactura && cabecera && (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
                  Cliente
                </Typography>
                <FacturaPOSClientePanel
                  cliente={posCliente}
                  onClienteSelect={() => {}}
                  readOnly={desdeFactura}
                  formaDePago={cabecera.formaDePago}
                  ordenCompra={cabecera.ordenCompra}
                />
              </Box>
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
                  <Typography variant="overline" color="text.secondary">
                    Artículos
                  </Typography>
                  {lineas.length > 0 && (
                    <Chip label={`${lineas.length} renglón${lineas.length > 1 ? 'es' : ''}`} size="small" />
                  )}
                </Stack>
                <FacturaPOSLineasGrid
                  lineas={lineas}
                  nroLista={nroLista}
                  onLineaChange={handleLineaChange}
                  onLineaFilled={handleLineaFilled}
                  onProductoIvaLoaded={registrarProductoIva}
                  onEliminarLinea={handleEliminarLinea}
                  onDuplicarLinea={handleDuplicarLinea}
                  onAgregarLineaVacia={handleAgregarLineaVacia}
                />
              </Box>
              {facturaTotales?.importeTotal != null && (
                <Typography variant="caption" color="text.secondary">
                  Total factura origen:{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    color={
                      Math.abs(totalesIva.total - facturaTotales.importeTotal) < 0.02
                        ? 'success.main'
                        : 'warning.main'
                    }
                  >
                    {formatCurrency(facturaTotales.importeTotal)}
                  </Typography>
                  {' · '}Total NC: {formatCurrency(totalesIva.total)}
                </Typography>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
              <FacturaPOSTotalesPanel
                subtotalNeto={subtotalNeto}
                porcDesc={0}
                porcDesc2={0}
                porcDesc3={0}
                onPorcDescChange={() => {}}
                impuestos={totalesIva.impuestosArray}
                montoIVA={totalesIva.montoIVA}
                total={totalesIva.total}
                observaciones={cabecera.observaciones}
                onObservacionesChange={(v) => updateCabecera({ observaciones: v })}
                cliente={posCliente}
                fechaEmision={cabecera.fechaEmision}
                onFechaEmisionChange={(ymd) => updateCabecera({ fechaEmision: ymd })}
                idCompania={cabecera.idCompania}
                onIdCompaniaChange={handleIdCompaniaChange}
                companias={companias}
                loadingCompanias={loadingCompanias}
                nroVendedor={posCliente?.nroVendedor ?? 0}
                nombreVendedorFallback={posCliente?.vendedorInfo?.nombreVendedor ?? undefined}
                onNroVendedorChange={(nro, nombre) => {
                  setPosCliente((prev) => {
                    if (!prev) return null;
                    if (!nro) return { ...prev, nroVendedor: undefined, vendedorInfo: undefined };
                    return {
                      ...prev,
                      nroVendedor: nro,
                      vendedorInfo: { nroVendedor: nro, nombreVendedor: nombre },
                    };
                  });
                }}
                codigoLista={obtenerCodigoListaCliente(posCliente) || ''}
                onCodigoListaChange={(codigo, descripcion) => {
                  const c = String(codigo ?? '').trim();
                  setPosCliente((prev) => {
                    if (!prev) return null;
                    if (!c) return { ...prev, nroLista: undefined, listaPrecio: undefined };
                    return {
                      ...prev,
                      nroLista: c,
                      listaPrecio: {
                        codigoLista: c,
                        descripcion: descripcion ?? prev.listaPrecio?.descripcion ?? null,
                      },
                    };
                  });
                }}
                formaDePago={cabecera.formaDePago}
                onFormaDePagoChange={(v) => updateCabecera({ formaDePago: v })}
                ordenCompra={cabecera.ordenCompra}
                onOrdenCompraChange={(v) => updateCabecera({ ordenCompra: v })}
                transporte={cabecera.transporte}
                onTransporteChange={(v) => updateCabecera({ transporte: v })}
                ubicacion={cabecera.ubicacion}
                onUbicacionChange={(v) => updateCabecera({ ubicacion: v })}
                saving={saving}
                onGuardar={() => void handleGuardar()}
                cantLineas={lineas.filter((l) => l.codigoArticulo.trim() && l.cantidad > 0).length}
                hideDraftButton
                saveLabel="Guardar Nota de Crédito"
                sucursales={sucursales}
                nroSuc={cabecera.nroSuc}
                onNroSucChange={(n) => updateCabecera({ nroSuc: n })}
                loadingSucursales={loadingSuc}
                emisionNombre={posCliente?.nombre ?? ''}
                onEmisionNombreChange={(v) =>
                  setPosCliente((p) => (p ? { ...p, nombre: v, razonSocial: v } : null))
                }
                emisionCuit={posCliente?.cuit ?? ''}
                onEmisionCuitChange={(v) => setPosCliente((p) => (p ? { ...p, cuit: v } : null))}
                emisionTipoIva={posCliente?.tipoIva ?? ''}
                onEmisionTipoIvaChange={(v) => setPosCliente((p) => (p ? { ...p, tipoIva: v } : null))}
              />
            </Box>
          </Grid>
        </Grid>
      )}

      <Dialog open={selectorOpen} onClose={() => setSelectorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Seleccionar factura de venta</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Buscar por texto (cliente, nombre, letra, punto venta, nro comprobante...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 1, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              label="Letra"
              value={searchLetra}
              onChange={(e) => setSearchLetra(e.target.value)}
              sx={{ minWidth: 80 }}
              inputProps={{ maxLength: 2 }}
            />
            <TextField
              size="small"
              label="Pto. venta / Nro Suc"
              value={searchNroSuc}
              onChange={(e) => setSearchNroSuc(e.target.value.replace(/\D/g, ''))}
              sx={{ minWidth: 100 }}
            />
            <TextField
              size="small"
              label="Nro Comprobante"
              value={searchNroComprobante}
              onChange={(e) => setSearchNroComprobante(e.target.value.replace(/\D/g, ''))}
              sx={{ minWidth: 120 }}
            />
            <TextField
              size="small"
              label="Nro. Cliente"
              value={searchNroCliente}
              onChange={(e) => setSearchNroCliente(e.target.value.replace(/\D/g, ''))}
              sx={{ minWidth: 120 }}
            />
            <TextField
              size="small"
              label="Fecha desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
            <TextField
              size="small"
              label="Fecha hasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />
          </Box>
          {loadingFacturas ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nro. interno</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Nro. Comprobante</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facturas.map((f) => (
                    <TableRow key={f.nroFormulario}>
                      <TableCell>{f.nroFormulario}</TableCell>
                      <TableCell>{formatDate(f.fechaEmision)}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{formatComprobante(f)}</TableCell>
                      <TableCell>{f.nroCliente ?? '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(f.importeTotal ?? 0)}</TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleSelectFactura(Number(f.nroFormulario))}
                        >
                          SELECCIONAR
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                count={totalFacturas}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 20]}
                labelRowsPerPage="Filas:"
              />
            </TableContainer>
          )}
          {!loadingFacturas && facturas.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No hay facturas para mostrar.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default NuevaNotaCreditoPOS;
